import React from 'react';
import { Phone, Mail, MessageCircle, Clock } from 'lucide-react';
import { trackPhoneClick, trackWhatsAppClick } from '../utils/analytics';

interface ContactMethodsProps {
  phoneNumber: string;
  email: string;
  whatsappMessage?: string;
  showWhatsApp?: boolean;
}

const ContactMethods: React.FC<ContactMethodsProps> = ({
  phoneNumber,
  email,
  whatsappMessage = "Hallo! Ich interessiere mich für Ihre Angebote.",
  showWhatsApp = true
}) => {
  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/\D/g, '').replace(/^0/, '49');
  };

  const formattedPhone = formatPhoneNumber(phoneNumber);
  const encodedMessage = encodeURIComponent(whatsappMessage);
  const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">Kontaktieren Sie uns</h3>

      <div className="space-y-4">
        {/* Telefon */}
        <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <Phone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">Telefon</h4>
            <a
              href={`tel:${phoneNumber}`}
              onClick={() => trackPhoneClick('contact_methods')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              {phoneNumber}
            </a>
          </div>
        </div>

        {/* WhatsApp */}
        {showWhatsApp && (
          <div className="flex items-center gap-4 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-800">WhatsApp</h4>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('contact_methods')}
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Direkt chatten
              </a>
            </div>
          </div>
        )}

        {/* E-Mail */}
        <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">E-Mail</h4>
            <a
              href={`mailto:${email}`}
              className="text-purple-600 hover:text-purple-800 font-medium"
            >
              {email}
            </a>
          </div>
        </div>

        {/* Öffnungszeiten */}
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
          <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">Öffnungszeiten</h4>
            <div className="text-sm text-gray-600">
              <p>Mo-Fr: 10:00 - 20:00 Uhr</p>
              <p>Sa: 10:00 - 18:00 Uhr</p>
              <p>So: Geschlossen</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactMethods;
